import { useCallback, useEffect, useRef, useState } from 'react'
import { CURSOR_LIST_PAGE_SIZE } from '../consts/api'

export type WindowedCursorListRange = {
  startIndex: number
  endIndex: number
}

export type CursorListFetchResult<T> = {
  items: T[]
  nextCursor: string | null
}

type UseWindowedCursorListOptions<T> = {
  resetKey: string | number
  fetchPage: (cursor: string | null) => Promise<CursorListFetchResult<T>>
}

function mergeById<T extends { id: number | string }>(base: T[], next: T[]): T[] {
  const ids = new Set(base.map((item) => item.id))
  const appended = next.filter((item) => !ids.has(item.id))
  return [...base, ...appended]
}

/**
 * Cursor infinite list: накапливает страницы, DOM виртуализирует Virtuoso.
 * Sliding-window trim отключён — firstItemIndex + trim давали дёргание при scroll up.
 */
export function useWindowedCursorList<T extends { id: number | string }>({
  resetKey,
  fetchPage,
}: UseWindowedCursorListOptions<T>) {
  const [items, setItems] = useState<T[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [isError, setIsError] = useState(false)
  const [firstPageItems, setFirstPageItems] = useState<T[]>([])

  const nextCursorRef = useRef<string | null>(null)
  const loadingRef = useRef(false)

  useEffect(() => {
    setItems([])
    setNextCursor(null)
    nextCursorRef.current = null
    setFirstPageItems([])
    setIsError(false)
    setIsInitialLoading(true)
    loadingRef.current = true

    fetchPage(null)
      .then((result) => {
        setItems(result.items)
        setFirstPageItems(result.items)
        setNextCursor(result.nextCursor)
        nextCursorRef.current = result.nextCursor
        setIsError(false)
      })
      .catch(() => {
        setIsError(true)
        setItems([])
      })
      .finally(() => {
        setIsInitialLoading(false)
        loadingRef.current = false
      })
  }, [fetchPage, resetKey])

  const loadMore = useCallback(async () => {
    const cursor = nextCursorRef.current
    if (!cursor || loadingRef.current) {
      return
    }

    loadingRef.current = true
    setIsFetchingMore(true)

    try {
      const result = await fetchPage(cursor)
      setItems((prev) => mergeById(prev, result.items))
      setNextCursor(result.nextCursor)
      nextCursorRef.current = result.nextCursor
      setIsError(false)
    } catch {
      setIsError(true)
    } finally {
      loadingRef.current = false
      setIsFetchingMore(false)
    }
  }, [fetchPage])

  const patchItems = useCallback((patcher: (items: T[]) => T[]) => {
    setItems((prev) => patcher(prev))
  }, [])

  return {
    items,
    hasMore: Boolean(nextCursor),
    isInitialLoading,
    isFetchingMore,
    isError,
    loadMore,
    patchItems,
    firstPageItems,
  }
}

export { CURSOR_LIST_PAGE_SIZE }
